import React, {useCallback, useEffect, useState, useMemo} from 'react'
import {
    Grid, Box, Button, CardContent, CardHeader, Typography, 
    FormControl, InputLabel, Select, MenuItem, Table, TableBody, 
    TableCell, TableContainer, TableHead, TableRow, Chip,
    Avatar,
} from "@mui/material";
import {useDispatch} from "react-redux";
import {Breadcrumbs, Loader} from "../../../../Components";
import {useSelector} from "react-redux";
import { useNavigate } from "react-router-dom";
import {GeneralCard} from "../../../../Components";
import { fetchRosterCalendar } from '../Redux/Action';
import { setSelectedDepartment } from '../../Department/Redux/Action';
import { styled, useTheme } from "@mui/material/styles";
import dayjs from 'dayjs';
import {cleanName} from "../../../../Setup/Config/formats";
import {getStatusColor} from "../../../../Setup";

// Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    maxHeight: 'calc(100vh - 320px)',
    borderRadius: theme.shape.borderRadius,
    '& .MuiTable-root': {
        '& .MuiTableCell-root': {
            borderColor: theme.palette.divider,
        },
        '& .MuiTableHead-root': {
            '& .MuiTableRow-root': {
                backgroundColor: theme.palette.primary.light,
                borderBottom: '1px dashed rgb(193 186 186 / 82%)',
                '& .MuiTableCell-root': {
                    backgroundColor: theme.palette.background.paper,
                    minWidth: 100,
                    borderLeft: '1px dashed rgb(193 186 186 / 82%)',
                    borderBottom: '1px dashed rgb(193 186 186 / 82%)',
                    padding: theme.spacing(1, 1.5),
                    '&:first-of-type': {
                        minWidth: 200,
                        borderLeft: 'none',
                    },
                },
            },
        },
        '& .MuiTableBody-root': {
            '& .MuiTableRow-root': {
                '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                },
                '& .MuiTableCell-root': {
                    borderLeft: '1px dashed rgb(193 186 186 / 82%)',
                    '&:first-of-type': {
                        borderLeft: 'none',
                    },
                },
            },
        },
    },
    '& .MuiAvatar-root': {
        width: 32,
        height: 32,
    },
    '& .MuiChip-root': {
        '&.MuiChip-sizeSmall': {
            fontSize: '0.75rem',
            height: 24,
        },
    },
    '& .day-label': {
        display: 'block',
        fontWeight: 400,
        fontSize: '0.85rem',
        textTransform: 'capitalize',
        color: theme.palette.text.secondary,
    },
    '& .day-date': {
        fontWeight: 600,
        fontSize: '1rem',
        color: theme.palette.text.primary,
    },
    '& .employee-name': {
        fontWeight: 600,
    },
    '& .department-name': {
        color: theme.palette.text.secondary,
    },
}));

// Status color map for roster statuses
const statusColorMap = {
    'ON_CALL': 'info',
    'OFF': 'error',
    '24_HOURS': 'warning',
    'ON_DUTY': 'success',
    'HOLIDAY': 'success',
    'WEEKEND': 'warning',
    'ON_SITE': 'success',
};

export const View = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const [isLoading, setIsLoading] = useState(false);
    const [, setErrors] = useState({});
    const [, setMessage] = useState('');
    const [, setStatus] = useState('info');
    const departments = useSelector((store) => store.app.config.departments);
    const items = useSelector((store) => store.rosters.calendar.list);
    const currentUser = useSelector((store) => store.auth.user);
    // eslint-disable-next-line no-unused-vars -- setter reserved for future week picker
    const [selectedWeek, setSelectedWeek] = useState(dayjs());
    const selectedDepartment = useSelector(state => state.departments?.selected);
    const holidays = useSelector((store) => store.app.config.holidays);
    
    // Get cell background color based on status
    const getCellColor = useCallback((status, level, effect) => {
        const colorName = statusColorMap[status];
        const color = getStatusColor(theme, colorName, level, effect);
        return color;
    }, [theme]);
    
    // Get MUI color name for Chip component
    const getRosterChipColor = (status) => {
        const colorMap = {
            'ON_CALL': 'info',
            'ON_SITE': 'success',
            'OFF': 'error',
            '24_HOURS': 'warning',
            'ON_DUTY': 'primary'
        };
        return colorMap[status] || 'default';
    };
    
    // Check if a date is a holiday
    const isHoliday = useCallback((date) => {
        if (!holidays || !Array.isArray(holidays)) return null;
        const dateStr = date.format('YYYY-MM-DD');
        return holidays.find(h => h.holiday_date === dateStr);
    }, [holidays]);

    // Auto-select "All Department" for admin users
    useEffect(() => {
        if (currentUser?.role_type === 'ADMIN' || currentUser?.role_type === 'SUPERADMIN') {
            //dispatch(setSelectedDepartment(null));
        } else if (currentUser?.department_id) {
            const dept = departments.find(dept => dept.id === currentUser.department_id);
            if (dept) {
                dispatch(setSelectedDepartment(dept));
            }
        }
    }, [currentUser, departments, dispatch]);

    // Get current week (Monday to Sunday)
    const currentWeek = useMemo(() => {
        const startOfWeek = dayjs().startOf('week').add(1, 'day'); // Monday
        const endOfWeek = dayjs().endOf('week').add(1, 'day'); // Sunday
        return { start: startOfWeek, end: endOfWeek };
    }, []);

    // Filter departments based on user role
    const availableDepartments = useMemo(() => {
        if (currentUser?.role_type === 'MANAGER') {
            // Manager sirf apna assigned department hi dekh sakta hai
            const dept = departments.find(dept => dept.manager_id === currentUser.id);
            if (dept && !selectedDepartment) {
                dispatch(setSelectedDepartment(dept));
            }
            return dept ? [dept] : [];
        }
        return departments; 
    }, [departments, currentUser, selectedDepartment, dispatch]);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        const departmentId = selectedDepartment?.id || '';
        const payload = {
            paginate:false,
            //with: ['status','user','user.department'],
            with: ['rosters.status'],
            month: selectedWeek.format('YYYY-MM'),
            week: selectedWeek.format('YYYY-MM-DD'),
            ...(departmentId && { department_id: departmentId })
        };
        
        await fetchRosterCalendar({ dispatch, filters: payload }).then((res) => {
            setIsLoading(false);
            setErrors(res.errors);
            setMessage(res.message);
            setStatus(res.status);
        });
    }, [dispatch, selectedDepartment, selectedWeek]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Generate week days (Monday to Sunday)
    const weekDays = useMemo(() => {
        const days = [];
        const startOfWeek = selectedWeek.startOf('week').add(1, 'day'); // Monday
        for (let i = 0; i < 7; i++) {
            days.push(startOfWeek.add(i, 'day'));
        }
        return days;
    }, [selectedWeek]);

    // Get roster status for specific date
    const getRosterStatus = (userId, date) => {
        const dateKey = date.format('YYYY-MM-DD');
        const user = items.find(item => item.id === userId);
        if (!user || !user.rosters) return '-';
        const roster = user.rosters[dateKey];
        return roster?.status?.name || '-';
    };

    // Get unique users from items
    const uniqueUsers = useMemo(() => {
        if (!items || items.length === 0) return [];
        return items.filter(item => item && item.id && item.first_name);
    }, [items]);

    // Handle department change
    const handleDepartmentChange = (e) => {
        const selectedDeptId = e.target.value;
        if (selectedDeptId === 'all') {
            dispatch(setSelectedDepartment(null));
            return;
        }
        const selectedDept = availableDepartments.find((dept) => dept.id === selectedDeptId);
        if (selectedDept) {
            dispatch(setSelectedDepartment(selectedDept));
        }
    };

    const handleManagementBtnClick = () => {
        // selectedDepartment already in Redux store
        navigate('/roster/manage');
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Box justifyContent={"space-between"} display={"flex"}>
                    <Breadcrumbs items={['DASHBOARD','ROSTER']} />
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={handleManagementBtnClick}
                    >
                        Roster Management
                    </Button>
                </Box>
            </Grid>
            <Grid item xs={12}>
                <GeneralCard>
                    <CardHeader 
                        title={
                            <Box>
                                <Typography variant="h6">Weekly Roster</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {currentWeek.start.format('MMM DD')} - {currentWeek.end.format('MMM DD, YYYY')}
                                </Typography>
                            </Box>
                        }
                        action={
                            <Box display="flex" gap={2} alignItems="center">
                                {/* Only show department selector for ADMIN/SUPERADMIN */}
                                {currentUser?.role_type && !['MANAGER', 'EMPLOYEE'].includes(currentUser.role_type) && (
                                    <FormControl size="large" sx={{ minWidth: 200 }}>
                                        <InputLabel>Department</InputLabel>
                                        <Select
                                            disabled={isLoading}
                                            value={selectedDepartment?.id || 'all'}
                                            onChange={handleDepartmentChange}
                                            label="Department"
                                            variant={"outlined"}
                                        >
                                            <MenuItem value="all" key="all">
                                                All Departments
                                            </MenuItem>
                                            {availableDepartments?.map((dept) => (
                                                <MenuItem key={dept.id} value={dept.id}>
                                                    {dept.department_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Box>
                        }
                    />
                    <CardContent sx={{ padding: '0px!important' }}>
                        <StyledTableContainer>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                        </TableCell>
                                        {weekDays.map((day) => {
                                            const holiday = isHoliday(day);
                                            const isWeekend = day.day() === 0 || day.day() === 6;
                                            
                                            const headerStatus = holiday ? 'HOLIDAY' : isWeekend ? 'WEEKEND' : null;
                                            return (
                                                <TableCell 
                                                    key={day.format('YYYY-MM-DD')} 
                                                    align="center"
                                                    sx={{
                                                        backgroundColor: headerStatus ? `${getCellColor(headerStatus, '0.4', 'lighten')}!important` : '#ffffff!important',
                                                        borderLeft: '1px dashed rgb(193 186 186 / 82%)',
                                                    }}
                                                >
                                                    <Box textAlign="center">
                                                        <Typography variant="caption" className="day-label">
                                                            {day.format('ddd')}
                                                        </Typography>
                                                        <Typography variant="body2" className="day-date">
                                                            {day.format('DD')}
                                                        </Typography>
                                                        
                                                    </Box>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                                <Loader isLoading={true} type={"linear"}/>
                                            </TableCell>
                                        </TableRow>
                                        ) : uniqueUsers && uniqueUsers.length > 0 ? (
                                        uniqueUsers.filter(user => user && user.id).map((user) => (
                                         <TableRow key={user.id} hover>
                                             <TableCell>
                                                 <Box display="flex" alignItems="center" gap={2}>
                                                     <Avatar
                                                         src={user?.avatar_url || '/images/user-icon-3.jpg'}
                                                         sx={{ width: 32, height: 32 }}
                                                     />
                                                     <Box>
                                                         <Typography variant="subtitle2" fontWeight={600}>
                                                             {user?.first_name || 'N/A'} {user?.last_name || ''}
                                                         </Typography>
                                                         <Typography variant="caption" color="text.secondary">
                                                             {user?.department || 'No Department'}
                                                         </Typography>
                                                     </Box>
                                                 </Box>
                                             </TableCell>
                                            {weekDays.map((day) => {
                                                const status = getRosterStatus(user.id, day);
                                                return (
                                                    <TableCell
                                                        key={day.format('YYYY-MM-DD')}
                                                        align="center"
                                                        sx={{
                                                            borderLeft: '1px dashed rgb(193 186 186 / 82%)',
                                                        }}
                                                    >
                                                         {status === '-' ? (
                                                             <Typography variant="body2" color="text.secondary">
                                                                 -
                                                             </Typography>
                                                         ) : (
                                                             <Chip
                                                                 label={cleanName(status || '')}
                                                                 size="small"
                                                                 color={getRosterChipColor(status)}
                                                                 variant="filled"
                                                             />
                                                         )}
                                                     </TableCell>
                                                 );
                                             })}

                                         </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {selectedDepartment ? 'No users found in selected department.' : 'No roster data available.'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </StyledTableContainer>
                    </CardContent>
                </GeneralCard>
            </Grid>
        </Grid>
    );
}
